import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { companyApi, matchingApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  ViewButton,
} from '@/components/ui';
import { PlanUpgradeState } from '@/features/billing/PlanUpgradeState';
import { MatchBadge } from '@/features/matching/MatchBadge';
import { MatchHighlights } from '@/features/matching/MatchBreakdown';
import { CandidateContactLine } from '@/features/candidates/CandidateContactLine';
import { MessageCandidateButton } from '@/features/messages/MessageCandidateButton';
import { errorMessage, isPlanLimitError } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, humanizeWorkMode } from '@/utils/format';

/** How many skills a row shows before folding the rest into "+N". */
const SKILLS_SHOWN = 3;

/**
 * Sourcing for one listing: students who have not applied but look like they
 * should have.
 *
 * Anyone already in the pipeline is absent — they belong on the applications
 * screen — and so is anyone who turned off "open to opportunities", because
 * being suggested to a recruiter is exactly the contact they opted out of.
 */
export function CompanySourcingPage() {
  const { id = '' } = useParams();

  const { data: internship } = useQuery({
    queryKey: ['company-internship', id],
    queryFn: () => companyApi.getInternship(id),
  });

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['candidate-matches', id],
    queryFn: () => matchingApi.getCandidateMatches(id, 20),
  });

  const rows = data ?? [];
  const listingHasNoSkills = (internship?.required_skills ?? []).length === 0;

  return (
    <div>
      <PageHeader
        title="Suggested candidates"
        description={
          internship ? `People who fit “${internship.title}”` : 'People who fit this role'
        }
        action={
          <LinkButton to={paths.company.internships} variant="secondary">
            <Icon name="internship" size={16} />
            Back to internships
          </LinkButton>
        }
      />

      {!isPending && !isError && listingHasNoSkills && (
        <Alert tone="info" className="mb-6" title="Add skills to this listing">
          Without required skills there is little to rank against, so these are simply the most
          complete profiles.{' '}
          <Link to={paths.company.internshipEdit(id)} className="font-medium underline">
            Edit the listing
          </Link>
          .
        </Alert>
      )}

      <ListCard
        state={
          isError ? (
            isPlanLimitError(error) ? (
              <PlanUpgradeState error={error} />
            ) : (
              <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
            )
          ) : isPending ? (
            <LoadingState label="Looking for people who fit…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="users" size={22} />}
              title="Nobody to suggest"
              description="Everyone we could find has either applied already or is not open to opportunities."
              action={
                <LinkButton to={paths.company.candidates} variant="secondary" size="sm">
                  Find candidates
                </LinkButton>
              }
            />
          ) : null
        }
      >
        <BoxList>
          {rows.map((row) => {
            const { candidate } = row;
            const study = [candidate.university, candidate.field_of_study]
              .filter(Boolean)
              .join(' · ');
            return (
              <BoxRow
                key={candidate.id}
                accent="brand"
                leading={<BoxAvatar name={candidate.full_name} round />}
                title={candidate.full_name}
                to={paths.company.candidateDetail(candidate.id)}
                badges={<MatchBadge match={row.match} />}
                subtitle={candidate.headline}
                facts={[
                  study && { icon: 'graduationCap', text: study },
                  candidate.graduation_year && {
                    icon: 'calendar',
                    text: `Graduating ${candidate.graduation_year}`,
                  },
                  candidate.preferred_work_mode && {
                    icon: 'briefcase',
                    text: humanizeWorkMode(candidate.preferred_work_mode),
                  },
                  candidate.location && { icon: 'home', text: candidate.location },
                  candidate.available_from && {
                    icon: 'calendarClock',
                    text: `Free from ${formatDate(candidate.available_from)}`,
                  },
                ]}
                actions={
                  <CardActions>
                    <MessageCandidateButton candidate={candidate} />
                    <ViewButton
                      to={paths.company.candidateDetail(candidate.id)}
                      title={`View ${candidate.full_name}'s profile`}
                      label="View profile"
                    />
                  </CardActions>
                }
              >
                <CandidateContactLine contact={candidate.contact} />
                <SkillBadges skills={candidate.top_skills} />
                <MatchHighlights match={row.match} className="mt-2" />
              </BoxRow>
            );
          })}
        </BoxList>
      </ListCard>
    </div>
  );
}

function SkillBadges({ skills }) {
  if (!skills?.length) return null;
  const hidden = skills.length - SKILLS_SHOWN;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1">
      {skills.slice(0, SKILLS_SHOWN).map((skill) => (
        <Badge key={skill} tone="brand">
          {skill}
        </Badge>
      ))}
      {hidden > 0 && (
        <Badge tone="neutral" title={skills.slice(SKILLS_SHOWN).join(', ')}>
          +{hidden}
        </Badge>
      )}
    </div>
  );
}
